import { CoeOfferDetailEntity } from '@genial/services';
import { selector, selectorFamily } from 'recoil';

import { ProductsType } from '../../../pages/BatchTicket/components/DynamicAsset/constants';
import { BatchTicketAssetsSelection } from '../../../pages/BatchTicket/contexts';
import { getOfferByDetailProductIdService } from '../../../pages/BatchTicket/services';
import { UpdateProductCartOfferParams } from '../../../pages/BatchTicket/utils/types';
import { AssetSelectedTypeMap } from '../../product/types';
import { productsCartState, productsCartStatusState } from '../atom';

export const updateProductsCartStatusSelector = selector({
  key: 'updateProductsCartStatusSelector',
  get: ({ get }) => get(productsCartStatusState),
  set: ({ set }, value) => set(productsCartStatusState, value),
});

export const addProductsCartSelector = selector({
  key: 'addProductsCartSelector',
  set: ({ get, set }, params) => {
    const { product, productType } = params as unknown as {
      productType: ProductsType;
      product: AssetSelectedTypeMap[ProductsType];
    };
    const prevState = get(productsCartState);

    const newState = {
      ...prevState,
      [productType]: {
        items: [...(prevState[productType]?.items || []), product],
      },
    };

    set(productsCartState, newState);
  },
  get: ({ get }) => get(productsCartState),
});

export const removeProductsCartSelector = selector({
  key: 'removeProductsCartSelector',
  set: ({ set, get }, params) => {
    const { idSelector, product, productType } = params as unknown as {
      productType: ProductsType;
      product: AssetSelectedTypeMap[ProductsType];
      idSelector: keyof AssetSelectedTypeMap[ProductsType];
    };

    const prevState = get(productsCartState);

    const newState = {
      ...prevState,
      [productType]: {
        items: prevState[productType].items.filter(
          (item) => item[idSelector] !== product[idSelector]
        ),
      },
    };

    set(productsCartState, newState);
  },
  get: ({ get }) => get(productsCartState),
});

export const productsCartCountSelector = selector({
  key: 'productsCartCountSelector',
  get: ({ get }) => {
    const productsCartList = get(productsCartState);

    const result = Object.values(productsCartList).reduce(
      (acc, investment) => {
        const hasItems = investment.items.length > 0;
        acc.assets += investment.items.length;
        acc.products += hasItems ? 1 : 0;
        return acc;
      },
      { assets: 0, products: 0 }
    );

    return result;
  },
});

export const investmentsSummarySelector = selector({
  key: 'investmentsSummarySelector',
  get: ({ get }) => {
    const investments = get(productsCartState);

    const calculateItemValue = (
      item: { orderValue: number; orderQuantity?: number },
      category: string
    ): number => {
      if (category === 'equity') {
        return item.orderValue * (item.orderQuantity ?? 1);
      }
      return item.orderValue;
    };

    const summary = Object.entries(investments).reduce(
      (acc, [category, investment]) => {
        const categoryTotal = investment.items.reduce(
          (sum, item) => sum + calculateItemValue(item, category),
          0
        );

        acc.totalsByCategory[category] = categoryTotal;
        acc.totalSum += categoryTotal;

        return acc;
      },
      { totalsByCategory: {} as Record<string, number>, totalSum: 0 }
    );

    return summary;
  },
});

export const addProductsListCartSelector = selector({
  key: 'addProductsListCartSelector',
  set: ({ set }, data) => {
    set(productsCartState, data as unknown as BatchTicketAssetsSelection);
  },
  get: ({ get }) => get(productsCartState),
});

export const CoeOfferDetailsQuery = selectorFamily<
  {
    data: CoeOfferDetailEntity;
  },
  { productId: string }
>({
  key: 'customersQuery',
  get: ({ productId }) => {
    return async () => {
      const response = await getOfferByDetailProductIdService({ productId });
      return response;
    };
  },
});

export const updateProductItemCartSelector = selector({
  key: 'updateProductItemCartSelector',
  set: ({ get, set }, params) => {
    const currentProductsCart = get(productsCartState);
    const { basketOfferId, ...rest } = params as unknown as UpdateProductCartOfferParams;

    const updatedProductsCart = Object.fromEntries(
      Object.entries(currentProductsCart).map(([category, categoryData]) => {
        return [
          category,
          {
            ...categoryData,
            items: categoryData.items.map((item) =>
              item.basketOfferId === basketOfferId
                ? { ...item, ...rest, orderValue: rest.amount, orderQuantity: rest.quantity }
                : item
            ),
          },
        ];
      })
    );

    set(productsCartState, updatedProductsCart as BatchTicketAssetsSelection);
  },
  get: ({ get }) => get(productsCartState),
});
